import { type ReactNode, useEffect, useRef, useState } from 'react';
import {
  TABS_BY_LIFECYCLE_PHASE,
  type LifecyclePhase,
  type WorkspaceTab,
} from '@scoutaround/script-breakdown-shared';

const TAB_LABEL: Record<WorkspaceTab, string> = {
  OVERVIEW: 'Overview',
  BILLING: 'Plan & billing',
  REFERENCES: 'References',
  SCRIPT_AND_SCENES: 'Script & Scenes',
  LOCATIONS: 'Locations',
  REQUESTS: 'Requests',
  SCHEDULE: 'Schedule',
  CHAT: 'Conversations',
  FILES: 'Files',
  TEAM: 'Team',
  ACTIVITY: 'Activity',
};

export interface WorkspaceShellProps {
  readonly phase: LifecyclePhase;
  /** The tabs to show. Defaults to the phase's set; the workspace narrows it
   *  further by PLAN, because offering a tab the production cannot use is an
   *  invitation to walk into a 403. */
  readonly tabs?: readonly WorkspaceTab[];
  readonly activeTab: WorkspaceTab;
  readonly onTabChange: (tab: WorkspaceTab) => void;
  readonly badges?: Partial<Record<WorkspaceTab, string>>;
  readonly header: ReactNode;
  readonly children: ReactNode;
}

/**
 * The workspace shell.
 *
 * THE TAB SET COMES FROM THE SHARED CONTRACT, never from a local list. A second
 * copy would drift from the one the API and its tests enforce, and the two would
 * disagree about which sections a phase has — with the UI winning, because it is
 * what the producer sees. Since 2026-10-29 every phase has every tab
 * (`TABS_BY_LIFECYCLE_PHASE`); the map is kept so a later narrowing is one edit.
 *
 * Keyed by `lifecycle_phase` rather than `operational_phase` (ADR 0008): the
 * lifecycle is the dimension a human deliberately set, so navigation changing
 * under someone because the system recalculated a suggestion would be startling.
 */
export function WorkspaceShell({
  phase,
  tabs: visibleTabs,
  activeTab,
  onTabChange,
  badges = {},
  header,
  children,
}: WorkspaceShellProps) {
  const tabs = visibleTabs ?? TABS_BY_LIFECYCLE_PHASE[phase];

  // Everything except Plan & billing, which is rendered as the gear on the title row.
  const rowTabs = tabs.filter((tab) => tab !== 'BILLING');

  /**
   * Keep the current section visible on a narrow screen.
   *
   * The tab strip scrolls horizontally rather than collapsing, and eleven tabs
   * do not fit on a phone. Land on Schedule from a link, or switch to Activity,
   * and the strip stays scrolled to the left: the section a producer is looking
   * at is off-screen, with nothing to suggest the row moves at all — so the
   * workspace reads as though it has four sections and no way to reach the
   * others.
   *
   * `nearest` rather than `center`, so nothing moves when the tab is already
   * visible; `block: 'nearest'` so the page itself never scrolls vertically
   * just to satisfy a horizontal strip.
   */
  const navRef = useRef<HTMLDivElement>(null);

  // A strip that scrolls sideways with no sign of it hides the tabs past the
  // edge — on a phone, most of them. Measured, not assumed: the fade is drawn
  // only while there is something past the edge to fade into.
  const [overflowing, setOverflowing] = useState(false);
  useEffect(() => {
    const el = navRef.current;
    if (!el) return;
    const measure = () => {
      setOverflowing(el.scrollWidth > el.clientWidth + 1);
    };
    measure();
    if (typeof ResizeObserver === 'undefined') return;
    const observer = new ResizeObserver(measure);
    observer.observe(el);

    return () => {
      observer.disconnect();
    };
  }, [tabs]);

  useEffect(() => {
    const active = navRef.current?.querySelector(`[data-testid="workspace-tab-${activeTab}"]`);

    // Optional call: jsdom has no `scrollIntoView`, and a missing scroll is not an error.
    active?.scrollIntoView?.({ inline: 'nearest', block: 'nearest', behavior: 'smooth' });
  }, [activeTab, tabs]);

  return (
    <div data-testid="workspace-shell" style={{ minHeight: '100%', background: 'var(--color-bg)' }}>
      <header
        style={{
          borderBottom: '2px solid var(--color-divider)',
          background: 'var(--color-surface)',
        }}
      >
        <div className="wrapx ws-shell-head" style={{ paddingTop: 28 }}>
          <div className="ws-shell-head-main">{header}</div>
          {/*
            PLAN & BILLING IS A SETTING, NOT A SECTION. The other tabs are
            places a producer works; this one is where they change what the
            production IS — so it sits apart, behind a gear on the title row
            (2026-11-04; at the end of the tab row before), where a phone
            shows it too instead of past the strip's edge.
            Its state now reads in the header beside the production's name,
            which is the half somebody needs at a glance; this is the half
            they need occasionally and deliberately.

            It keeps its tab, its slug and its URL — only the presentation
            moved. A bookmark still opens it, and it is still in the phase's
            tab set, so nothing about authorization or plan gating changed.
          */}
          {tabs.includes('BILLING') ? (
            <button
              type="button"
              className="ptab ws-settings"
              data-on={activeTab === 'BILLING' ? '1' : '0'}
              data-testid="workspace-tab-BILLING"
              aria-current={activeTab === 'BILLING' ? 'page' : undefined}
              aria-label={TAB_LABEL.BILLING}
              title={TAB_LABEL.BILLING}
              onClick={() => {
                onTabChange('BILLING');
              }}
            >
              {/* Inline SVG rather than an icon font: this app ships no icon
                  set, and a missing glyph renders as a blank box that looks
                  like a bug. */}
              <svg
                width="17"
                height="17"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
                aria-hidden="true"
              >
                <circle cx="12" cy="12" r="3" />
                <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 1 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 1 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 1 1-2.83-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 1 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 1 1 2.83-2.83l.06.06A1.65 1.65 0 0 0 9 4.6a1.65 1.65 0 0 0 1-1.51V3a2 2 0 1 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 1 1 2.83 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 1 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z" />
              </svg>
              {/* The word stays: a lone gear is a guess, and this one leads to
                  money. It is a small label beside the name, not a tab. */}
              <span style={{ fontSize: 12 }}>Settings</span>
            </button>
          ) : null}
        </div>

        <nav aria-label="Workspace sections" className="wrapx" style={{ position: 'relative' }}>
          {overflowing ? <span className="ptab-strip-fade" aria-hidden="true" data-testid="workspace-tab-strip-fade" /> : null}
          <div
            ref={navRef}
            className="ptab-strip"
            data-overflow={overflowing ? '1' : '0'}
            data-testid="workspace-tab-strip"
            style={{
              display: 'flex',
              gap: 26,
              borderBottom: '2px solid var(--color-divider)',
              overflowX: 'auto',
            }}
          >
            {rowTabs.map((tab) => (
              <button
                key={tab}
                type="button"
                className="ptab"
                data-on={activeTab === tab ? '1' : '0'}
                data-testid={`workspace-tab-${tab}`}
                aria-current={activeTab === tab ? 'page' : undefined}
                onClick={() => {
                  onTabChange(tab);
                }}
              >
                {TAB_LABEL[tab]}
                {badges[tab] ? (
                  <span
                    className="stag"
                    data-testid={`workspace-tab-badge-${tab}`}
                    style={{
                      marginLeft: 7,
                      background: 'var(--color-accent-100)',
                      color: 'var(--color-accent-600)',
                    }}
                  >
                    {badges[tab]}
                  </span>
                ) : null}
              </button>
            ))}
          </div>
        </nav>
      </header>

      <main className="wrapx" style={{ paddingTop: 28, paddingBottom: 64 }}>
        {children}
      </main>
    </div>
  );
}
